import React, { useState, useEffect } from 'react';
import DestinatariBuilder, { TIPI_DESTINATARIO, RUOLI_DISPONIBILI } from './DestinatariBuilder';

// Eventi trigger disponibili
const EVENTI_TRIGGER = [
  {
    category: 'Scadenze',
    events: [
      { value: 'scadenza_30', label: '30 giorni prima', description: 'Invia 30 giorni prima della scadenza' },
      { value: 'scadenza_7', label: '7 giorni prima', description: 'Invia 7 giorni prima della scadenza' },
      { value: 'scadenza_3', label: '3 giorni prima', description: 'Invia 3 giorni prima della scadenza' },
      { value: 'scadenza_1', label: '1 giorno prima', description: 'Invia il giorno prima della scadenza' },
      { value: 'scadenza_oggi', label: 'Giorno stesso', description: 'Invia il giorno della scadenza' },
      { value: 'scadenza_passata', label: 'Giorno dopo', description: 'Invia se scadenza non evasa' },
      { value: 'scadenza_passata_7', label: '7 giorni dopo', description: 'Sollecito dopo 7 giorni dalla scadenza' }
    ]
  },
  {
    category: 'Contratti',
    events: [
      { value: 'contratto_scadenza_30', label: 'Contratto: 30 giorni prima', description: 'Contratto in scadenza tra 30 giorni' },
      { value: 'contratto_scadenza_7', label: 'Contratto: 7 giorni prima', description: 'Contratto in scadenza tra 7 giorni' }
    ]
  },
  {
    category: 'Interventi',
    events: [
      { value: 'intervento_completato', label: 'Intervento completato', description: 'Notifica quando un intervento viene completato' },
      { value: 'intervento_annullato', label: 'Intervento annullato', description: 'Notifica quando un intervento viene annullato' }
    ]
  }
];

// Ore disponibili per invio
const ORE_INVIO = Array.from({ length: 24 }, (_, i) => ({
  value: `${String(i).padStart(2, '0')}:00`,
  label: `${String(i).padStart(2, '0')}:00`
}));

/**
 * Componente per configurare una regola di invio automatico
 */
function RegolaConfigurator({
  value = {},
  onChange,
  templates = [],
  mode = 'create', // create, edit
  className = ''
}) {
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Valori di default
  const regola = {
    nome: '',
    templateId: '',
    evento: '',
    destinatari: { tipo: '', ruoli: [], emailCustom: [], cc: [], ccRuoli: [] },
    oraInvio: '09:00',
    attivo: true,
    priorita: 100,
    ...value
  };

  // Handler aggiornamento
  const handleChange = (field, newValue) => {
    onChange({ ...regola, [field]: newValue });
  };

  // Trova l'evento selezionato
  const eventoSelezionato = EVENTI_TRIGGER
    .flatMap(cat => cat.events)
    .find(e => e.value === regola.evento);

  // Determina il contesto in base all'evento
  const getContextType = () => {
    if (regola.evento?.startsWith('contratto_')) return 'contratto';
    if (regola.evento?.startsWith('intervento_')) return 'intervento';
    return 'scadenza';
  };

  return (
    <div className={`regola-configurator space-y-6 ${className}`}>
      {/* Nome regola */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Nome Regola *
        </label>
        <input
          type="text"
          value={regola.nome}
          onChange={(e) => handleChange('nome', e.target.value)}
          placeholder="Es: Reminder 30 giorni fornitori"
          className="w-full px-4 py-2 border rounded-lg focus:ring-primary-500 focus:border-primary-500"
        />
      </div>

      {/* Selezione evento trigger */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Evento Trigger *
        </label>
        <div className="space-y-4">
          {EVENTI_TRIGGER.map(category => (
            <div key={category.category}>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                {category.category}
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                {category.events.map(event => (
                  <button
                    key={event.value}
                    type="button"
                    onClick={() => handleChange('evento', event.value)}
                    className={`text-left p-3 rounded-lg border-2 transition-all ${
                      regola.evento === event.value
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <div className="font-medium text-sm text-gray-900">{event.label}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{event.description}</div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Selezione template */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Template Email *
        </label>
        {templates.length > 0 ? (
          <select
            value={regola.templateId}
            onChange={(e) => handleChange('templateId', e.target.value)}
            className="w-full px-4 py-2 border rounded-lg focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">Seleziona template...</option>
            {templates.map(template => (
              <option key={template.id} value={template.id}>
                {template.nome} - {template.oggetto}
              </option>
            ))}
          </select>
        ) : (
          <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
            <p className="text-sm text-amber-700">
              Nessun template disponibile. Crea prima un template email.
            </p>
          </div>
        )}
      </div>

      {/* Configurazione destinatari */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Destinatari *
        </label>
        <DestinatariBuilder
          value={regola.destinatari}
          onChange={(dest) => handleChange('destinatari', dest)}
          contextType={getContextType()}
          showCC={true}
        />
      </div>

      {/* Ora invio */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Ora Invio
          </label>
          <select
            value={regola.oraInvio}
            onChange={(e) => handleChange('oraInvio', e.target.value)}
            className="w-full px-4 py-2 border rounded-lg focus:ring-primary-500 focus:border-primary-500"
          >
            {ORE_INVIO.map(ora => (
              <option key={ora.value} value={ora.value}>
                {ora.label}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-1">
            Le email vengono inviate all'ora specificata
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Stato
          </label>
          <div className="flex items-center gap-4 h-[42px]">
            <label className="inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={regola.attivo}
                onChange={(e) => handleChange('attivo', e.target.checked)}
                className="sr-only peer"
              />
              <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-500"></div>
              <span className="ml-3 text-sm font-medium text-gray-700">
                {regola.attivo ? 'Attiva' : 'Disattivata'}
              </span>
            </label>
          </div>
        </div>
      </div>

      {/* Impostazioni avanzate */}
      <div className="border-t pt-4">
        <button
          type="button"
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
        >
          <svg
            className={`w-4 h-4 transition-transform ${showAdvanced ? 'rotate-90' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          Impostazioni Avanzate
        </button>

        {showAdvanced && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg space-y-4">
            {/* Priorità */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Priorità Esecuzione
              </label>
              <input
                type="number"
                min="1"
                max="1000"
                value={regola.priorita}
                onChange={(e) => handleChange('priorita', parseInt(e.target.value) || 100)}
                className="w-32 px-4 py-2 border rounded-lg focus:ring-primary-500 focus:border-primary-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Valori più bassi = priorità maggiore (default: 100)
              </p>
            </div>

            {/* Note / Descrizione */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Note
              </label>
              <textarea
                value={regola.note || ''}
                onChange={(e) => handleChange('note', e.target.value)}
                rows={2}
                placeholder="Note interne sulla regola..."
                className="w-full px-4 py-2 border rounded-lg focus:ring-primary-500 focus:border-primary-500 text-sm"
              />
            </div>
          </div>
        )}
      </div>

      {/* Riepilogo configurazione */}
      <div className="p-4 bg-gradient-to-r from-primary-50 to-blue-50 rounded-lg border border-primary-100">
        <h4 className="text-sm font-semibold text-gray-900 mb-2">Riepilogo Regola</h4>
        <div className="space-y-1 text-sm text-gray-700">
          {regola.nome && (
            <p><span className="font-medium">Nome:</span> {regola.nome}</p>
          )}
          {eventoSelezionato && (
            <p><span className="font-medium">Trigger:</span> {eventoSelezionato.description}</p>
          )}
          {regola.templateId && templates.find(t => t.id === regola.templateId) && (
            <p><span className="font-medium">Template:</span> {templates.find(t => t.id === regola.templateId)?.nome}</p>
          )}
          {regola.destinatari?.tipo && (
            <p>
              <span className="font-medium">Destinatari:</span>{' '}
              {TIPI_DESTINATARIO.find(t => t.value === regola.destinatari.tipo)?.label}
              {regola.destinatari.tipo === 'ruolo' && regola.destinatari.ruoli?.length > 0 && (
                <span className="text-gray-500"> ({regola.destinatari.ruoli.join(', ')})</span>
              )}
              {regola.destinatari.tipo === 'custom' && regola.destinatari.emailCustom?.length > 0 && (
                <span className="text-gray-500"> ({regola.destinatari.emailCustom.length} email)</span>
              )}
            </p>
          )}
          <p><span className="font-medium">Ora invio:</span> {regola.oraInvio}</p>
          <p>
            <span className="font-medium">Stato:</span>{' '}
            <span className={regola.attivo ? 'text-green-600' : 'text-red-600'}>
              {regola.attivo ? 'Attiva' : 'Disattivata'}
            </span>
          </p>
        </div>
      </div>

      {/* Validazione */}
      {(!regola.nome || !regola.evento || !regola.templateId || !regola.destinatari?.tipo) && (
        <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
          <p className="text-sm text-amber-700">
            <span className="font-medium">Campi mancanti:</span>{' '}
            {[
              !regola.nome && 'Nome',
              !regola.evento && 'Evento trigger',
              !regola.templateId && 'Template',
              !regola.destinatari?.tipo && 'Destinatari'
            ].filter(Boolean).join(', ')}
          </p>
        </div>
      )}
    </div>
  );
}

export default RegolaConfigurator;
export { EVENTI_TRIGGER, ORE_INVIO };
